import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, Pause, Square, Volume2, VolumeX, X, ChevronDown, ChevronUp, Music2,
  SkipBack, SkipForward,
} from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import ExpandedPlayer from "./ExpandedPlayer";

const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

export default function MiniPlayer() {
  const { current, playing, muted, volume, progress, duration, notice, minimized, expanded, queue,
    toggle, stop, toggleMute, setVolume, seek, close, next, prev,
    setNotice, setMinimized, setExpanded } = usePlayer();
  const hasQueue = queue.length > 1;

  return (
    <AnimatePresence>
      {notice && (
        <motion.div
          key="player-notice"
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[9980] bg-[#0a0a0a] border-t border-white/15"
          data-testid="player-notice"
        >
          <div className="max-w-[1440px] mx-auto px-5 md:px-12 py-5 flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-mono text-[9px] tracking-[0.3em] text-gold mb-1.5 break-words">{notice.title?.toUpperCase()}</p>
              <p className="text-white/70 text-sm">{notice.message}</p>
            </div>
            <div className="flex items-center gap-5">
              {notice.cta && (
                <Link to={notice.cta.to} data-testid="player-notice-cta" onClick={() => setNotice(null)}
                  className="border border-gold/60 text-gold font-mono text-[10px] tracking-[0.25em] px-6 py-3 hover:bg-gold hover:text-black transition-colors">
                  {notice.cta.label}
                </Link>
              )}
              <button onClick={() => setNotice(null)} data-testid="player-notice-close" aria-label="Dismiss"
                className="text-white/50 hover:text-white transition-colors"><X size={18} /></button>
            </div>
          </div>
        </motion.div>
      )}

      {current && !notice && expanded && <ExpandedPlayer key="expanded-player" />}

      {current && !notice && !expanded && minimized && (
        <motion.button
          key="mini-restore"
          initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
          onClick={() => setMinimized(false)}
          data-testid="mini-player-restore" aria-label="Restore player"
          className="fixed bottom-5 right-5 z-[9980] w-12 h-12 rounded-full bg-gold text-black flex items-center justify-center shadow-[0_8px_30px_rgba(212,175,55,0.35)]"
        >
          <Music2 size={17} className={playing ? "animate-pulse" : ""} />
        </motion.button>
      )}

      {current && !notice && !expanded && !minimized && (
        <motion.div
          key="mini-player"
          initial={{ y: 90, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 90, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[9980] bg-[#0a0a0a]/95 backdrop-blur-md border-t border-white/15"
          data-testid="mini-player"
        >
          <div className="max-w-[1440px] mx-auto px-3 sm:px-4 md:px-12 py-3.5 flex items-center gap-2.5 sm:gap-4 md:gap-5">
            {current.artwork && (
              <button onClick={() => setExpanded(true)} className="hidden sm:block shrink-0" aria-label="Expand player">
                <img src={current.artwork} alt="" className="w-11 h-11 object-cover rounded-[6px]" />
              </button>
            )}
            {hasQueue && (
              <button onClick={prev} data-testid="mini-player-prev" aria-label="Previous track"
                className="w-9 h-9 rounded-full border border-white/20 text-white/60 hidden sm:flex items-center justify-center hover:border-white/50 hover:text-white transition-colors shrink-0">
                <SkipBack size={12} />
              </button>
            )}
            <button onClick={toggle} data-testid="mini-player-toggle" aria-label={playing ? "Pause" : "Play"}
              className="w-10 h-10 rounded-full border border-gold/60 text-gold flex items-center justify-center hover:bg-gold hover:text-black transition-colors shrink-0">
              {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
            </button>
            {hasQueue && (
              <button onClick={next} data-testid="mini-player-next" aria-label="Next track"
                className="w-9 h-9 rounded-full border border-white/20 text-white/60 flex items-center justify-center hover:border-white/50 hover:text-white transition-colors shrink-0">
                <SkipForward size={12} />
              </button>
            )}
            <button onClick={stop} data-testid="mini-player-stop" aria-label="Stop"
              className="w-9 h-9 rounded-full border border-white/20 text-white/60 hidden md:flex items-center justify-center hover:border-white/50 hover:text-white transition-colors shrink-0">
              <Square size={11} />
            </button>
            <div className="hidden md:flex items-center gap-2 shrink-0">
              <button onClick={toggleMute} data-testid="mini-player-mute" aria-label={muted ? "Unmute" : "Mute"} aria-pressed={muted}
                className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors shrink-0 ${
                  muted ? "border-gold/60 text-gold" : "border-white/20 text-white/60 hover:border-white/50 hover:text-white"}`}>
                {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
              </button>
              <input type="range" min={0} max={1} step={0.02} value={muted ? 0 : volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                data-testid="mini-player-volume" aria-label="Volume"
                className="w-20 h-[3px] appearance-none bg-white/15 accent-[#d4af37] cursor-pointer" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-3">
                {current.trackSlug ? (
                  <Link to={`/track/${current.trackSlug}`} className="font-serif text-base text-white truncate hover:text-gold transition-colors" data-testid="mini-player-title">
                    {current.title}
                  </Link>
                ) : (
                  <p className="font-serif text-base text-white truncate" data-testid="mini-player-title">{current.title}</p>
                )}
                {current.releaseTitle && (
                  <span className="font-mono text-[8px] tracking-[0.25em] text-white/35 truncate hidden md:inline">
                    {current.releaseTitle.toUpperCase()}
                  </span>
                )}
                {current.teaser && (
                  <span className="font-mono text-[8px] tracking-[0.3em] text-gold/80 border border-gold/30 px-2 py-0.5 shrink-0 hidden sm:inline">
                    TEASER{current.teaserDuration ? ` · ${current.teaserDuration}″` : ""}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="font-mono text-[9px] text-white/40 w-8">{fmt(progress)}</span>
                <input type="range" min={0} max={duration || 0} value={progress} step={0.1}
                  onChange={(e) => seek(Number(e.target.value))}
                  data-testid="mini-player-seek" aria-label="Seek"
                  className="flex-1 h-[3px] appearance-none bg-white/15 accent-[#d4af37] cursor-pointer" />
                <span className="font-mono text-[9px] text-white/40 w-8">{fmt(duration)}</span>
              </div>
            </div>
            <button onClick={() => setExpanded(true)} data-testid="mini-player-expand" aria-label="Expand player"
              className="text-white/50 hover:text-gold transition-colors shrink-0">
              <ChevronUp size={18} />
            </button>
            <button onClick={() => setMinimized(true)} data-testid="mini-player-minimize" aria-label="Minimize player"
              className="text-white/50 hover:text-white transition-colors shrink-0 hidden sm:block">
              <ChevronDown size={18} />
            </button>
            <button onClick={close} data-testid="mini-player-close" aria-label="Close player"
              className="text-white/50 hover:text-white transition-colors shrink-0">
              <X size={18} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
